// src/theme/tokens.ts
//
// The non-palette design tokens: motion, the typographic ramp, page geometry,
// and the accessors for the surface treatment.
//
// ─── WHAT WAS REMOVED, AND WHY ──────────────────────────────────────────────
// This file arrived from the product SPA carrying that app's furniture — a
// collapsible drawer rail, a bottom navigation bar, the Fintelligent launcher
// pill and its clearance arithmetic, a Monaco code-surface palette, and a
// shared icon-button shape for four "registry" toolbars. None of those surfaces
// exist in this repo; every one of those exports had zero importers. They are
// gone rather than kept "in case", because a token nothing reads is a token
// nobody maintains, and the next person cannot tell it apart from one that is
// load-bearing.
//
// `space` went with them for a different reason: it was an 8px scale duplicating
// `theme.spacing`, which is already 8px. Two definitions of the same quantity
// drift toward disagreeing, and this codebase argues that everywhere else.

import type { Theme } from '@mui/material/styles';
import { surface } from './surfaces';

/**
 * Radius belongs to the surface treatment (see ./surfaces) — geometry and
 * surface are one decision. Prefer `theme.surface.radius`; this re-export is
 * for the few consumers with no theme in scope.
 */
export const radius = surface.radius;

export const motion = {
  duration: {
    instant: 80,
    fast: 120,
    base: 180,
    slow: 240,
  },
  easing: {
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    decelerate: 'cubic-bezier(0, 0, 0, 1)',
    accelerate: 'cubic-bezier(0.3, 0, 1, 1)',
    /** Material-smooth. For menus and dialogs that should feel fluid, not bouncy. */
    smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
} as const;

/**
 * Page geometry.
 *
 * `maxWidth` is deliberately absent: this is a wall display showing tables up
 * to 860px of minimum width side by side with charts, and capping the column
 * would waste the screen it was chosen for. Padding is the only constraint.
 */
export const layout = {
  header: {
    /** The chrome bar. `InternalBand` sits above it and is 24px + 4px border. */
    height: 48,
    /** The section tab strip below the header, when a section has tabs. */
    tabsHeight: 40,
  },
  band: {
    height: 24,
    borderWidth: 4,
  },
  page: {
    // ── PÍXELES, DICHOS EN VOZ ALTA ────────────────────────────────────────
    // Todo este bloque son píxeles —`header.height: 48` se consume como
    // `minHeight`, y `band` habla de «24px + 4px border»— pero estos dos se
    // consumían como `px`/`py`, que MUI SÍ pasa por `theme.spacing`. Con la
    // escala por omisión de 8, `20` no eran 20px sino 160, y `16` eran 128:
    // la consola llevaba 128px de espacio muerto encima de cada pantalla en
    // escritorio y 160 a cada lado.
    //
    // Pasó desapercibido porque el resultado se ve como una decisión de
    // diseño: márgenes generosos y contenido centrado. Solo se nota al medirlo.
    // Con la unidad escrita, `px` los deja pasar tal cual y el número dice lo
    // que vale.
    paddingX: { xs: '12px', md: '20px' },
    paddingY: { xs: '12px', md: '16px' },
    /** The gutter between panels in a grid. Matches `theme.spacing(2)`. */
    gap: 16,
  },
} as const;

/**
 * The typographic ramp the app was missing.
 *
 * Before this was wired, the wordmark and every panel title were both
 * `variant="h4"` at 0.95rem, and there was no page-level heading anywhere in
 * the application — so no screen had an `<h1>` and nothing established rank.
 *
 * `figure` is DELIBERATELY larger than `pageTitle`. In an instrument the number
 * is the subject and the chrome is the label; they never compete because a
 * figure always sits beneath an `eyebrow` while a page title stands alone.
 */
export const typeScale = {
  pageTitle: {
    fontSize: '1.375rem', // 22px
    lineHeight: 1.2,
    letterSpacing: '-0.015em',
    fontWeight: 600,
  },
  sectionTitle: {
    fontSize: '1.0625rem', // 17px
    lineHeight: 1.3,
    letterSpacing: '-0.005em',
    fontWeight: 600,
  },
  eyebrow: {
    fontSize: '0.6875rem', // 11px
    lineHeight: 1,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    fontWeight: 600,
  },
  figure: {
    fontSize: '1.625rem', // 26px
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
    fontWeight: 700,
  },
  figureSm: {
    fontSize: '1.125rem', // 18px
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
    fontWeight: 600,
  },
} as const;

/**
 * The active surface preset for a theme, with the module constant as a fallback.
 *
 * ── THE FALLBACK IS NOT DEFENSIVE, IT IS REQUIRED ───────────────────────────
 * `auth/staffGuard.test.tsx` renders `<NoAccessScreen/>` with NO ThemeProvider,
 * deliberately — the screen's whole contract is that it says the same thing in
 * every circumstance. MUI's default theme carries no `surface`, so reading
 * `theme.surface.chrome(...)` directly would throw inside a currently-green
 * test. And the fallback is not an approximation: the preset is a module
 * constant, so it IS the value the themed path would have returned.
 */
export function surfaceOf(theme: Theme): typeof surface {
  return theme.surface ?? surface;
}

/** Ambient chrome — the header and the section bar. Translucent on purpose. */
export function glassSurface(theme: Theme) {
  return surfaceOf(theme).chrome(theme.palette.mode);
}

/** A content-bearing overlay — a menu or a dialog. Nearly opaque. */
export function overlaySurface(theme: Theme) {
  return surfaceOf(theme).overlay(theme.palette.mode);
}

/** A surface nested inside another — a filter bar inside a panel. */
export function glassInset(theme: Theme) {
  return surfaceOf(theme).panel(theme.palette.mode);
}

/** The scrim behind a modal. */
export function scrimSurface(theme: Theme) {
  return surfaceOf(theme).scrim(theme.palette.mode);
}
